import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating different snapping behaviors.
 *
 * Features:
 * - Function A (sky): Handle with INTEGER snapping - snaps to whole numbers
 * - Function B (indigo): Handle with HALF-GRID snapping (0.5 resolution)
 * - Function C (teal): Handle with NO snapping - free movement
 *
 * Drag each handle to compare the snapping behaviors.
 */
export const TEST_SNAPPING_TRANSLATION_PRESET: GraphpadWidgetConfig = {
  id: 'test-snapping-translation',
  type: 'graphpad',
  params: {
    // Function A - integer snapping
    a: { type: 'quadratic-vertex', params: { a: 1, h: -4, k: 2 } },
    'a-handle': { type: 'handle', params: { of: 'a', behavior: 'translate' } },

    // Function B - half-grid snapping (0.5)
    b: { type: 'quadratic-vertex', params: { a: 1, h: 0, k: 1 } },
    'b-handle': { type: 'handle', params: { of: 'b', behavior: 'translate-y' } },

    // Function C - no snapping (free)
    c: { type: 'quadratic-vertex', params: { a: 1, h: 4, k: 0 } },
    'c-handle': { type: 'handle', params: { of: 'c', behavior: 'translate-x' } },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -8, xEnd: 8, yStart: -6, yEnd: 8 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      // Function A - sky color, integer snapping (1x1 grid)
      a: { curve: { color: 'sky' } },
      'a-handle': {
        point: {
          color: 'sky',
          snapping: { grid: { enabled: true, sizeX: 1, sizeY: 1 } },
        },
      },

      // Function B - indigo color, 0.5 grid snapping
      b: { curve: { color: 'indigo' } },
      'b-handle': {
        point: {
          color: 'indigo',
          snapping: { grid: { enabled: true, sizeX: 0.5, sizeY: 0.5 } },
        },
      },

      // Function C - teal color, no snapping (no snapping config = free movement)
      c: { curve: { color: 'teal' } },
      'c-handle': {
        point: { color: 'teal' },
      },
    },
  },
  behavior: {},
};

/**
 * Snapping behavior summary:
 *
 * - Sky handle (left): INTEGER snapping
 *   Coordinates snap to ..., -2, -1, 0, 1, 2, ...
 *
 * - indigo handle (middle): HALF-GRID snapping
 *   Coordinates snap to ..., -1, -0.5, 0, 0.5, 1, 1.5, ...
 *
 * - teal handle (right): NO snapping
 *   Completely free movement, coordinates can be any value
 *
 * Drag each handle and observe how the movement differs!
 */
